import React, { useState } from "react";
import { Link } from "react-router-dom";
import katex from "katex";
import "katex/dist/katex.min.css";

const PLACEHOLDER_NORMAL = `Homework,40%,85%\nMidterm,30%,75%\nFinal Project,20%,100%`;
const PLACEHOLDER_INFER  = `Homework,17/20\nMidterm,45/60\nFinal Project,20/20`;

function fmt(n) {
  return parseFloat(n.toFixed(2));
}

function fmtW(w) {
  return parseFloat(w.toFixed(4));
}

function MathBlock({ latex }) {
  const html = katex.renderToString(latex, { displayMode: true, throwOnError: false });
  return <div className="gc-latex" dangerouslySetInnerHTML={{ __html: html }} />;
}

function parseGrades(input, inferWeights) {
  const lines = input.trim().split("\n");
  if (lines[0].toLowerCase().trim() === "category,weight,grade") lines.shift();

  const categories = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    const parts = trimmed.split(",");
    const category = parts[0].trim();
    let parsedWeight, parsedGrade, scoreDisplay, latexTerm;

    if (inferWeights) {
      if (parts.length < 2) throw new Error("Invalid format");
      const gradeStr = (parts.length >= 3 ? parts[2] : parts[1]).trim();
      if (!gradeStr.includes("/")) {
        throw new Error(`"${category}" has no fraction — Infer Weights requires fraction grades (e.g. 17/20)`);
      }
      const [numStr, denStr] = gradeStr.split("/");
      const numVal = parseFloat(numStr.trim());
      const denVal = parseFloat(denStr.trim());
      parsedWeight = denVal;
      parsedGrade  = numVal / denVal;
      scoreDisplay = `${fmt(numVal)} / ${fmt(denVal)} = ${fmt(parsedGrade * 100)}%`;
      latexTerm    = `\\left(\\dfrac{${fmt(numVal)}}{${fmt(denVal)}} \\cdot ${fmtW(parsedWeight)}\\right)`;
    } else {
      if (parts.length < 3) throw new Error("Invalid format");
      const weightStr = parts[1].trim();
      const gradeStr  = parts[2].trim();

      parsedWeight = weightStr.includes("%")
        ? parseFloat(weightStr.replace("%", "")) / 100
        : parseFloat(weightStr);

      if (gradeStr.includes("/")) {
        const [numStr, denStr] = gradeStr.split("/");
        const numVal = parseFloat(numStr.trim());
        const denVal = parseFloat(denStr.trim());
        parsedGrade  = numVal / denVal;
        scoreDisplay = `${fmt(numVal)} / ${fmt(denVal)} = ${fmt(parsedGrade * 100)}%`;
        latexTerm    = `\\left(\\dfrac{${fmt(numVal)}}{${fmt(denVal)}} \\cdot ${fmtW(parsedWeight)}\\right)`;
      } else if (gradeStr.includes("%")) {
        parsedGrade  = parseFloat(gradeStr.replace("%", "")) / 100;
        scoreDisplay = `${fmt(parsedGrade * 100)}%`;
        latexTerm    = `\\left(${fmt(parsedGrade)} \\cdot ${fmtW(parsedWeight)}\\right)`;
      } else {
        parsedGrade  = parseFloat(gradeStr);
        scoreDisplay = `${fmt(parsedGrade * 100)}%`;
        latexTerm    = `\\left(${fmt(parsedGrade)} \\cdot ${fmtW(parsedWeight)}\\right)`;
      }
    }

    if (isNaN(parsedWeight) || isNaN(parsedGrade)) throw new Error("Invalid numbers");
    categories.push({ name: category, weight: parsedWeight, grade: parsedGrade, scoreDisplay, latexTerm });
  }

  if (categories.length === 0) throw new Error("No data");

  const weightSum  = categories.reduce((s, c) => s + c.weight, 0);
  const multiplier = 1.0 / weightSum;

  const breakdown = categories.map(c => ({
    name: c.name,
    scoreDisplay: c.scoreDisplay,
    weightPct: c.weight / weightSum * 100,
  }));

  const totalGrade = categories.reduce((s, c) => s + c.grade * c.weight, 0) * multiplier;
  const finalGrade = Math.round(totalGrade * 100 * 100) / 100;

  const numerator    = categories.map(c => c.latexTerm).join(" + ");
  const denominator  = categories.map(c => fmtW(c.weight)).join(" + ");
  const latexFormula = `\\dfrac{${numerator}}{${denominator}}`;

  return { finalGrade, breakdown, latexFormula };
}

export default function GradeCalculator() {
  const [input, setInput]               = useState("");
  const [inferWeights, setInferWeights] = useState(false);
  const [result, setResult]             = useState(null);
  const [error, setError]               = useState("");
  const [copied, setCopied]             = useState(false);

  const handleCalculate = () => {
    try {
      if (!input.trim()) { setError("Please enter some grade data."); return; }
      setResult(parseGrades(input, inferWeights));
      setError("");
    } catch (e) {
      setError(e.message || "Could not parse input. Check the format.");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleCalculate();
  };

  const handleClear = () => { setResult(null); setInput(""); setError(""); setCopied(false); };

  const handleRogerHub = () => {
    navigator.clipboard.writeText(String(result.finalGrade));
    window.open("https://rogerhub.com/final-grade-calculator/", "_blank");
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="gc-page">
      <div className="container">
        <div className="gc-inner">
          <Link to="/" className="back-link">← back</Link>
          <div>
            <h1 className="gc-title">grade calculator</h1>
            <p className="gc-subtitle">Weighted grade calculator with CSV-based input.</p>
          </div>

          <div className="gc-form">
            <div className="gc-field">
              <label className="gc-label">
                {inferWeights ? "Category, Grade" : "Category, Weight, Grade"}
              </label>
              <textarea
                className="gc-textarea"
                value={input}
                onChange={e => { setInput(e.target.value); setResult(null); }}
                onKeyDown={handleKeyDown}
                placeholder={inferWeights ? PLACEHOLDER_INFER : PLACEHOLDER_NORMAL}
                rows={8}
                spellCheck={false}
                autoFocus
              />
            </div>
            <label className="gc-checkbox-row">
              <input
                type="checkbox"
                checked={inferWeights}
                onChange={e => setInferWeights(e.target.checked)}
              />
              Infer Weights
              <span
                className="gc-tooltip-trigger"
                data-tip="Use the denominator of each fraction grade as its weight (e.g. 17/20 → weight of 20%). Useful when your gradebook shows categories and score with a fraction and you're lazy."
              >ⓘ</span>
            </label>
            {error && <p className="gc-error">{error}</p>}
            <div className="gc-btn-row">
              <button className="gc-btn" onClick={handleCalculate}>calculate</button>
              {(result || input) && (
                <button className="gc-btn gc-btn-secondary" onClick={handleClear}>clear</button>
              )}
            </div>
            <p className="gc-kbd-hint">or ⌘↵ / Ctrl+↵</p>
          </div>

          {result && (
            <div className="gc-result">
              <div className="gc-final">
                <span className="gc-final-label">current grade =</span>
                <span className="gc-final-value">{result.finalGrade}%</span>
              </div>
              <div className="gc-breakdown">
                <div className="gc-breakdown-header">
                  <span>category</span>
                  <span>score</span>
                  <span>contribution</span>
                </div>
                {result.breakdown.map((c, i) => (
                  <div className="gc-breakdown-row" key={i}>
                    <span className="gc-breakdown-name">{c.name}</span>
                    <span className="gc-breakdown-detail">{c.scoreDisplay}</span>
                    <span className="gc-breakdown-weight">{c.weightPct.toFixed(1)}%</span>
                  </div>
                ))}
              </div>
              <button className="gc-btn" onClick={handleRogerHub}>
                {copied ? "copied!" : "copy final grade & open in rogerhub ↗"}
              </button>
            </div>
          )}

          <div className="gc-format-hint">
            <div className="gc-hint-section">
              <p className="gc-hint-label">format</p>
              <p className="gc-hint-text">
                Weights can be percentages (40%) or decimals (0.4). Grades can be percentages (85%),
                fractions (17/20), or decimals (0.85). Weights don't need to sum to 100%. Useful for
                calculating your grade mid-semester before all categories are graded and you want
                to know what you need on your final exam.
              </p>
            </div>
            <div className="gc-hint-section">
              <p className="gc-hint-label">how it works</p>
              <p className="gc-hint-text">
                Each score is first converted to a percentage (e.g. 15/20 → 75%). That
                percentage is multiplied by the category weight, all products are summed, then
                divided by the total weight, not by 100%.
              </p>
              {result && <MathBlock latex={result.latexFormula} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
